import { Option as DefaultOption, AppRoute } from '@chegg-wt/core/types/core'
import React, { useEffect, useRef, useState, KeyboardEvent } from 'react'
import AppLink from '@chegg-wt/core/components/AppLink'

import { useThrottle } from '@chegg-wt/core/hooks/functions'
import { useOnClickOutside } from '@chegg-wt/core/hooks/useOnClickOutside'
import { useSelector } from 'react-redux'
import { currentCitationSourceTypeSelector } from '../../state/citation/selectors'
import { topSourcesTypeById } from '../../utils/sourceTypes'
import * as Styled from './styled'
import * as TestId from './testIds'
import * as Id from './ids'

interface Option extends DefaultOption {
  linkedRoute?: AppRoute
}

export interface TriggerProps {
  /** the handler that opens the dropdown */
  openDropdown: () => void
  /** the option that is currently selected, so it can be set as the text of the trigger */
  selected: Option
  /** whether the dropdown is currently open, so the trigger can change display options */
  isOpen: boolean
}

/** small: 160px, medium: 300px, large 400px */
/** default to medium */
export type Width = 'small' | 'medium' | 'large'
/** default to right */
export type DropdownAlignment = 'left' | 'right'

export interface Props {
  onClose?: () => void
  /** callback when option is selected */
  onSelect: (id: string, name: string) => void
  /** optional callback when bottom of list is reached */
  onScrollToBottom?: () => void
  /** list of options */
  options: Option[]
  /** passed in element that opens dropdown */
  trigger: React.FC<TriggerProps>
  /** default option that is selected */
  defaultOption?: Option
  /** width of dropdown */
  width?: Width
  /** alignment of dropdown */
  dropdownAlignment?: DropdownAlignment
  /** description of dropdown options, for use in id / aria label */
  type: string
  /** should render options as link vs default button type */
  renderOptionAsLink?: boolean
  /** should visually hide dropdown on initial render */
  isDropdownVisuallyHiddenOnInitialRender?: boolean
}

const Select: React.FC<Props> = ({
  children,
  onClose,
  onSelect,
  onScrollToBottom,
  options,
  trigger: Trigger,
  defaultOption = { id: null, name: '' },
  width = 'medium',
  dropdownAlignment = 'right',
  type,
  renderOptionAsLink = false,
  isDropdownVisuallyHiddenOnInitialRender = false,
}) => {
  const initialState = {
    isOpen: false,
    isVisuallyHidden: isDropdownVisuallyHiddenOnInitialRender,
    currentOptions: options,
    selected: defaultOption,
    highlighted: -1,
  }

  // whether the dropdown is open
  const [isOpen, setIsOpen] = useState(initialState.isOpen)
  // whether the dropdown is visually hidden
  const [isVisuallyHidden, setIsVisuallyHidden] = useState(initialState.isVisuallyHidden)
  // which option is selected
  const [selected, setSelected] = useState(initialState.selected)
  // which option is highlighted
  const [highlighted, setHighlighted] = useState(initialState.highlighted)

  const currentCitationSourceType = useSelector(currentCitationSourceTypeSelector)

  // reset the filter and highlight, and close the dropdown
  const reset = () => {
    setIsOpen(initialState.isOpen)
    setIsVisuallyHidden(initialState.isVisuallyHidden)
    setHighlighted(initialState.highlighted)
    if (onClose) onClose()
  }

  // handle selecting an option
  const handleSelect = (id: string, name: string) => {
    setSelected({ id, name })
    reset()
    onSelect(id, name)
  }

  // open the dropdown when called by the trigger
  const openDropdown = () => {
    setIsOpen((s) => !s)
    setIsVisuallyHidden(false)
  }

  // set the highlighted option and scroll to it
  const highlightedOption = useRef<HTMLButtonElement & HTMLAnchorElement>()
  const optionList = useRef<HTMLDivElement>()

  // scroll to highlighted option
  useEffect(() => {
    if (!optionList.current) return
    if (!optionList.current.scrollTo) optionList.current.scrollTo = () => {}
    if (!highlightedOption.current) return
    const top = highlightedOption.current.offsetTop
    const height = highlightedOption.current.offsetHeight * 2
    optionList.current.scrollTo({ behavior: 'smooth', top: top - height })
    highlightedOption.current.focus()
  }, [highlighted])

  // reset selected when default option changes
  useEffect(() => {
    if (defaultOption.id && defaultOption.id !== selected.id) {
      setSelected(defaultOption)
    }
  }, [defaultOption])

  useEffect(() => {
    if (topSourcesTypeById(currentCitationSourceType)) {
      setSelected(initialState.selected)
    }
  }, [currentCitationSourceType])

  // handle arrow navigation, enter select, and escape close
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!isOpen) return null
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault()
        return setHighlighted((v) => (v > 0 ? v - 1 : v))
      case 'ArrowDown':
        e.preventDefault()
        return setHighlighted((v) => (v < options.length - 1 ? v + 1 : v))
      case 'Escape':
        return reset()
      case 'Enter': {
        const option =
          highlighted > -1 && highlighted < options.length ? options[highlighted] : null
        if (!option) {
          return null
        }
        return handleSelect(option.id?.toString(), option.name)
      }
      default:
        return null
    }
  }

  // click outside effect
  const wrapper = useRef<HTMLDivElement>()
  useOnClickOutside(wrapper, () => {
    if (isOpen) {
      reset()
    }
  })
  // onScrollToBottom behavior
  const throttledOnScrollToBottom = useThrottle(onScrollToBottom, 500)
  const handleScroll = (e: any) => {
    const {
      target: { scrollHeight, scrollTop, clientHeight },
    } = e
    if (!onScrollToBottom) return
    const closeToBottom = scrollHeight - scrollTop <= clientHeight + 50
    if (isOpen && closeToBottom) throttledOnScrollToBottom()
  }

  const option = ({ id, name, linkedRoute }, i: number) => {
    const isSelected = selected.id === id
    const isHighlighted = highlighted === i
    const props = {
      selected: isSelected,
      'aria-selected': isSelected,
      'aria-label': `${name} option`,
      role: 'option',
      onMouseDown: () => handleSelect(id, name),
      ref: isHighlighted ? highlightedOption : null,
      onFocus: () => setHighlighted(i),
      'data-test': TestId.SELECT_OPTION_ID(id),
      'data-highlighted': isHighlighted,
      width,
    }
    const child = (
      <>
        <Styled.SelectedContainer>
          {isSelected && <Styled.Checkmark aria-label="Selected option" />}
        </Styled.SelectedContainer>
        <Styled.OptionName title={name}>{name}</Styled.OptionName>
      </>
    )

    if (renderOptionAsLink) {
      return (
        <AppLink route={linkedRoute} key={id}>
          <Styled.LinkOption tabIndex={0} {...props}>
            {child}
          </Styled.LinkOption>
        </AppLink>
      )
    }

    return (
      <Styled.ButtonOption key={id} {...props}>
        {child}
      </Styled.ButtonOption>
    )
  }

  return (
    <Styled.DropdownWrapper
      id={Id.SELECT_WRAPPER_ID(type)}
      ref={wrapper}
      onKeyDown={handleKeyDown}
      data-test={TestId.SELECT_DROPDOWN_WRAPPER_ID}
    >
      <Trigger
        aria-haspopup
        isOpen={isOpen}
        aria-label={isOpen ? 'close menu' : 'open menu'}
        aria-expanded={isOpen}
        openDropdown={openDropdown}
        selected={selected}
      />
      {(isOpen || isVisuallyHidden) && (
        <Styled.DropdownContainer
          data-test={`select-${type}-dropdown`}
          aria-label={`Select ${type}`}
          width={width}
          alignment={dropdownAlignment}
          visuallyHidden={isVisuallyHidden}
        >
          {children}
          <Styled.OptionsContainer
            role="listbox"
            id="listbox"
            aria-label={`Select ${type}`}
            ref={optionList}
            onScroll={handleScroll}
          >
            {options.map(option)}
          </Styled.OptionsContainer>
        </Styled.DropdownContainer>
      )}
    </Styled.DropdownWrapper>
  )
}

export default Select
